import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generate } from 'short-uuid';

import { GET_BUSINESS_ACCOUNT_STATUS } from 'store/queries';
import { ADD_PAYOUT_AND_BALANCE, VOID_BOOKING, UPDATE_PAYMENTS, CANCEL_BOOKING_ITEMS, CONFIRM_BOOKING } from 'store/mutations';
import { codes, businessAccountStatuses, statusShortCode } from 'constants/payment';

const CANCELLATION_REASON = 'Voyager Cooling Off Period';

const getBafAccountNumber = ({ method, reference }) => (method === codes.BUSINESS_ACCOUNT ? reference?.split('-')[0] : undefined);

const useVoidBooking = ({ bookingReference, booking }) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessageId, setErrorMessageId] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const bafPayments = (booking.payments || []).filter((p) => p.method === codes.BUSINESS_ACCOUNT && !p.isPayout);
  const bafAccountNumbers = [...new Set(bafPayments.map(getBafAccountNumber).filter(Boolean))];

  const [fetchAccountStatus] = useLazyQuery(GET_BUSINESS_ACCOUNT_STATUS, {
    fetchPolicy: 'no-cache'
  });

  const [voidBooking] = useMutation(VOID_BOOKING, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });
  const [addPayoutAndBalance] = useMutation(ADD_PAYOUT_AND_BALANCE);
  const [updatePayments] = useMutation(UPDATE_PAYMENTS);
  const [cancelBookingItems] = useMutation(CANCEL_BOOKING_ITEMS);
  const [confirmBooking] = useMutation(CONFIRM_BOOKING);

  const handleClick = async () => {
    if (bafAccountNumbers.length > 0) {
      setIsChecking(true);
      try {
        const statuses = await Promise.all(
          bafAccountNumbers.map((accountNumber) =>
            fetchAccountStatus({ variables: { accountNumber } }).then((res) => res?.data?.businessAccountStatus)
          )
        );

        const allLive = statuses.every((s) => s === businessAccountStatuses.LIVE);

        if (!allLive) {
          setErrorMessageId('voidBooking.error.bafInactive');
          setErrorOpen(true);
          return;
        }
      } catch {
        setErrorMessageId('voidBooking.error.generic');
        setErrorOpen(true);
        return;
      } finally {
        setIsChecking(false);
      }
    }

    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const { data: voidData } = await voidBooking({ variables: { bookingReference, cancellationReason: CANCELLATION_REASON } });

      const payments = voidData?.voidBooking?.payments || [];

      const s3Refund = payments.find((p) => p.method === codes.REFUND && p.isSuccessful && !p.refundProcessTimestamp);

      if (s3Refund) {
        const refundablePayments = payments.filter((p) => p.isSuccessful && !p.isPayout && !p.isBalance && p.method !== codes.REFUND);

        for (const payment of refundablePayments) {
          await addPayoutAndBalance({
            variables: {
              bookingReference,
              amount: Math.abs(payment.amount),
              currency: payment.currency,
              method: payment.method,
              externalReference: getBafAccountNumber(payment),
              reference: `${s3Refund.refId}-${generate()}`
            }
          });
        }

        await updatePayments({
          variables: {
            bookingReference,
            payments: [{ ref: Number(s3Refund.refId), status: statusShortCode.success, refundProcessed: true }]
          }
        });
      }

      await cancelBookingItems({
        variables: {
          bookingReference,
          overrideReason: CANCELLATION_REASON,
          overrideAftersalesRules: false
        }
      });

      await confirmBooking({
        variables: {
          bookingReference,
          sendConfirmationEmail: false
        }
      });

      setConfirmOpen(false);
    } catch (error) {
      setConfirmOpen(false);
      const s3Code = error?.graphQLErrors?.[0]?.extensions?.inventoryErrorCode;
      if (s3Code === 'operation_not_allowed_if_aftersales_operation_exists') {
        setErrorMessageId('voidBooking.error.aftersales');
      } else if (s3Code === 'operation_not_allowed_for_sales_channel' || s3Code === 'agent_user_not_allowed_to_void_items') {
        setErrorMessageId('voidBooking.error.noPermission');
      } else {
        setErrorMessageId('voidBooking.error.generic');
      }
      setErrorOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const closeConfirm = () => setConfirmOpen(false);

  const closeError = () => {
    setErrorOpen(false);
    setErrorMessageId(null);
  };

  const isLoading = isChecking || isConfirming;

  return {
    isConfirmOpen,
    isErrorOpen,
    errorMessageId,
    isLoading,
    isConfirming,
    handleClick,
    handleConfirm,
    closeConfirm,
    closeError
  };
};

export default useVoidBooking;
